// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';

import { useAppDispatch, useAppSelector } from 'Hooks';

import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import LabelComponent from './LabelComponent';

import {
  CreateAnnotationThunk,
  EditAnnotationThunk,
  DeleteAnnotationThunk,
} from '../esriSplitViewerThunk';

interface LabelData {
  id: number;
  point: __esri.Point;
  text: string;
  attributes: any;
}

interface Props {
  view: __esri.MapView | __esri.SceneView;
  active: boolean;
  annotations?: any[];
  orderId: string;
  newLabel: boolean;
  viewWidthNew: any;
  otherwidgetactive:boolean;
  basemapactive : boolean
}

const LabelEditor: React.FC<Props> = ({ view, active, annotations = [], orderId, newLabel, viewWidthNew, otherwidgetactive, basemapactive }) => {
  const [labels, setLabels] = useState<LabelData[]>([]);
  const graphicsLayerRef = useRef<__esri.GraphicsLayer | null>(null);
  // The view the label layer was added to: a new view (the scene rebuilt after a Retry) gets its own layer
  const layerViewRef = useRef<__esri.MapView | __esri.SceneView | null>(null);
  const idRef = useRef(1);
  const loadedRef = useRef(false);
  const dispatch = useAppDispatch();
  const previousOrderId = useRef<any>(null);
  const [LabelWidth, setLabelWidths] = useState(null);
  const orders = useAppSelector((state) => state.order?.list);
  const newLabelref = useRef(false);

  // INIT GRAPHICS LAYER & CLICK CONTROL USING newLabel

  useEffect(() => {
    // The scene effect destroys the old view before its null reaches this component; a destroyed view has no map
    if (!view || view.destroyed || !view.map) return;
    const tempWidth = view.width;

    setLabelWidths(tempWidth);
    // Detect view type
    const isMapView = view instanceof MapView;
    const isSceneView = view instanceof SceneView;

    // If SceneView → disable adding labels
    if (isSceneView && newLabel && !otherwidgetactive) {
      newLabelref.current = true;
    }

    // Create the graphics layer once per view; a new view starts its labels over on its own layer
    if (!graphicsLayerRef.current || layerViewRef.current !== view) {
      if (graphicsLayerRef.current) {
        setLabels([]);
        idRef.current = 1;
        loadedRef.current = false;
      }
      const layer = new GraphicsLayer({
        id: 'annotation-label-layer',
        visible: active,
      });
      view.map.add(layer);
      graphicsLayerRef.current = layer;
      layerViewRef.current = view;
    } else {
      graphicsLayerRef.current.visible = active;
    }

    let handle: __esri.Handle | null = null;

    // Not active → no listener
    if (!active) {
      if (view?.container) view.container.style.cursor = 'default';
      const tempWidth = view.width;
      setLabelWidths(tempWidth);
      return () => handle?.remove?.();
    }

    // If newLabel = false → no click listener
    if (!newLabel) {
      if (view?.container) view.container.style.cursor = 'default';
      const tempWidth = view.width;
      setLabelWidths(tempWidth);
      return () => handle?.remove?.();
    }

    // Only allow adding label in MapView
    if (isSceneView && !otherwidgetactive) {
      if (view?.container) view.container.style.cursor = 'crosshair';
      const tempWidth = view.width;
      setLabelWidths(tempWidth);
    }

    // Enable adding mode
    if (view?.container) view.container.style.cursor = 'crosshair';

    let labeladded = false;
    let newannotation;
    handle = view.on('click', (event) => {
      newannotation = (window as any).newannotationclicked;
      if (newannotation) {
        labeladded = false;
      }
      newLabelref.current = true;
      if (basemapactive === false && newLabelref.current === true) {
        if (labeladded) return;
        if ((window as any).basemapOpened === true) return;
        addNewLabel(event.mapPoint, labeladded);
        labeladded = true;
        (window as any).newannotationclicked = false;
        if (view?.container) {
          newLabelref.current = false;
          view.container.style.cursor = 'default';
        }
      }
    });
    return () => {
      handle?.remove?.();
      if (view?.container) view.container.style.cursor = 'default';
    };
  }, [view, active, newLabel]);

  useEffect(() => {
    // Only reset when changing to a DIFFERENT order
    if (previousOrderId.current !== orderId) {
      previousOrderId.current = orderId;

      setLabels([]);
      idRef.current = 1;
      loadedRef.current = false;
      graphicsLayerRef.current?.removeAll();
    }
    window.dispatchEvent(new Event('toggle-existing-annotations'));
  }, [orderId]);

  useEffect(() => {
    if (!view || view.destroyed || !annotations.length) return;
    if (loadedRef.current) return;

    loadedRef.current = true;

    const layer = graphicsLayerRef.current;
    if (!layer) return;

    const loaded: LabelData[] = [];
    const selectedOrder = orders?.find((o: any) => o._id === orderId);
    if (!selectedOrder) return;
    annotations.forEach((a: any) => {
      const mapPoint = new Point({
        latitude: a.geometry.coordinates[1],
        longitude: a.geometry.coordinates[0],
      });

      // eslint-disable-next-line no-plusplus
      const localId = idRef.current++;
      const attrs = {
        localId,
        id: a.properties.id,
        text: a.properties.text,
        color: a.properties.color,
        latitude: String(mapPoint.latitude),
        longitude: String(mapPoint.longitude),
        ownerOrder: a.properties.ownerOrder || selectedOrder._id,
        ownerProject: a.properties.ownerProject || selectedOrder.ownerProject,
        ownerOrganization: a.properties.ownerOrganization || selectedOrder.ownerOrganization,
        email: a.properties.email,
      };

      loaded.push({
        id: localId,
        point: mapPoint,
        text: attrs.text,
        attributes: attrs,
      });

      const g = new Graphic({
        geometry: mapPoint,
        attributes: attrs,
        symbol: { type: 'simple-marker', color: [0, 0, 0, 1], size: 6 },
      });

      layer.add(g);
    });

    setLabels(loaded);
  }, [annotations, view]);

  // New Label
  const addNewLabel = async (mapPoint: __esri.Point, _labeladded) => {
    if (!newLabel || _labeladded) return;

    // eslint-disable-next-line no-plusplus
    const localId = idRef.current++;

    const selectedOrder = orders?.find((o: any) => o._id === orderId);

    if (!selectedOrder) return;

    const attrs = {
      localId,
      id: undefined,
      latitude: String(mapPoint.latitude),
      longitude: String(mapPoint.longitude),
      text: 'My Label',
      color: '#01A1DD',
      ownerOrder: selectedOrder._id,
      ownerProject: selectedOrder.ownerProject,
      ownerOrganization: selectedOrder.ownerOrganization,
      email: selectedOrder.ownerEmail,
    };

    setLabels((prev) => [
      ...prev,
      { id: localId, point: mapPoint, text: attrs.text, attributes: attrs },
    ]);

    const g = new Graphic({
      geometry: mapPoint,
      attributes: attrs,
      symbol: { type: 'simple-marker', color: [0, 0, 0, 1], size: 6 },
    });

    graphicsLayerRef.current?.add(g);
  };
  // Delete Label
  const deleteLabel = async (localId: number) => {
    const layer = graphicsLayerRef.current;

    setLabels((prev) => prev.filter((l) => l.id !== localId));

    if (layer) {
      const g = layer.graphics.find((gr) => gr.attributes?.localId === localId);
      if (g) layer.remove(g);
    }

    const label = labels.find((l) => l.id === localId);

    if (label?.attributes.id) {
      try {
        const DeleteOrderId = label.attributes.order_id || label.attributes.ownerOrder;
        await dispatch(DeleteAnnotationThunk(label.attributes.id, DeleteOrderId));
      } catch { /* empty */ }
    }
  };

  //  EDIT LABEL

  const editLabel = async (updatedAttrs: any) => {
    const { localId, ...attrs } = updatedAttrs;
    if (
      attrs.text === null ||
    attrs.text === undefined ||
    attrs.text.trim() === ''
    ) {
      return;
    }

    let finalAttrs;

    if (attrs.id) {
      const allowed = ['text', 'color', 'email'];
      const filtered = Object.fromEntries(
        Object.entries(attrs).filter(([k]) => allowed.includes(k)),
      );
      const res = await dispatch(EditAnnotationThunk(filtered, attrs.id));

      finalAttrs = { ...res.data.properties, localId, id: attrs.id };
    } else {
      const res = await dispatch(CreateAnnotationThunk(attrs));
      finalAttrs = { ...attrs, id: res.data.properties.id, localId };
    }

    setLabels((prev) =>
      prev.map((l) =>
        (l.id === localId
          ? { ...l, text: finalAttrs.text, attributes: finalAttrs }
          : l)));

    const layer = graphicsLayerRef.current;
    if (layer) {
      const g = layer.graphics.find(
        (gr) => gr.attributes?.localId === localId,
      );
      if (g) g.attributes = { ...finalAttrs };
    }
  };

  const updateLabelColor = (updatedAttrs: any, newColor: string) => {
    editLabel({ ...updatedAttrs, color: newColor });
  };

  if (!active) return null;

  // RENDER LABEL COMPONENTS

  return (
    <>
      {labels.map((label) => (
        <LabelComponent
          key={label.id}
          view={view}
          mapPoint={label.point}
          color={label.attributes.color}
          text={label.attributes.text}
          attributes={label.attributes}
          onDelete={() => deleteLabel(label.id)}
          onEdit={(newText) => editLabel({ ...label.attributes, text: newText })}
          onColorChange={(newColor) => updateLabelColor({ ...label.attributes, color: newColor }, newColor)}
          viewWidth={viewWidthNew}
        />
      ))}
    </>
  );
};

export default LabelEditor;
